import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaBell, FaBellSlash, FaUserPlus, FaEnvelope } from 'react-icons/fa';
import './css_pages/Notifications.css';
import {
  getNotificationsByStore,
  getUsersForStore,
  toggleNotification,
  updateRecipients
} from '../services/notificationsService';
import { useStore } from '../contexts/StoreContext';

function Notifications() {
  const [notifications, setNotifications]         = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState(null);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [availableUsers, setAvailableUsers]        = useState([]);
  const [selectedUserIds, setSelectedUserIds]      = useState([]);
  const [savingRecipients, setSavingRecipients]    = useState(false);
  const [userSearch, setUserSearch]                = useState('');

  const { selectedStore } = useStore();

  // ============================================================
  // FETCH
  // ============================================================
  const fetchNotifications = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoading(true); setError(null);
      const response = await getNotificationsByStore(selectedStore.id);
      if (response.success) setNotifications(response.data);
    } catch {
      setError('Error loading notifications');
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // ============================================================
  // TOGGLE ACTIVE
  // ============================================================
  const handleToggle = async (notification) => {
    try {
      setLoading(true);
      const response = await toggleNotification(notification.id_notification);
      if (response.success) {
        setNotifications(prev => prev.map(n =>
          n.id_notification === notification.id_notification
            ? { ...n, is_active: response.data.is_active }
            : n
        ));
      }
    } catch {
      alert('Error updating notification status');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // ABRIR MODAL DE RECIPIENTS
  // ============================================================
  const handleOpenRecipients = async (notification) => {
    try {
      setLoading(true);
      setSelectedNotification(notification);
      setSelectedUserIds(notification.recipients.map(r => r.id_user));
      setUserSearch('');

      const response = await getUsersForStore(selectedStore.id);
      if (response.success) setAvailableUsers(response.data);

      setShowRecipientsModal(true);
    } catch {
      alert('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseRecipients = () => {
    setShowRecipientsModal(false);
    setSelectedNotification(null);
    setSelectedUserIds([]);
    setAvailableUsers([]);
    setUserSearch('');
  };

  const handleToggleUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSaveRecipients = async () => {
    if (!selectedNotification) return;
    try {
      setSavingRecipients(true);
      const response = await updateRecipients(selectedNotification.id_notification, selectedUserIds);
      if (response.success) {
        // Actualizar localmente
        setNotifications(prev => prev.map(n =>
          n.id_notification === selectedNotification.id_notification
            ? { ...n, recipients: response.data }
            : n
        ));
        handleCloseRecipients();
      }
    } catch {
      alert('Error saving recipients');
    } finally {
      setSavingRecipients(false);
    }
  };

  const filteredUsers = availableUsers.filter(u =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // ============================================================
  // NO STORE
  // ============================================================
  if (!selectedStore) {
    return (
      <div className="notif-page">
        <div className="page-header"><h1>Notifications</h1></div>
        <div className="no-store-message"><p>Please select a store from the sidebar</p></div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="notif-page">

      {/* HEADER */}
      <div className="page-header sticky-header">
        <div className="header-left">
          <h1>Notifications</h1>
          <span className="store-badge">{selectedStore.name}</span>
        </div>
      </div>

      <p className="notif-subtitle">
        Configure which users receive email alerts for each event type.
      </p>

      {error && <div className="error-message">{error}<button onClick={fetchNotifications}>Retry</button></div>}

      {/* LISTA DE NOTIFICACIONES */}
      <div className="notif-list">
        {loading && notifications.length === 0 && <div className="loading">Loading...</div>}

        {notifications.length === 0 && !loading && (
          <div className="notif-empty">No notification types configured.</div>
        )}

        {notifications.map((notif) => (
          <div key={notif.id_notification} className={`notif-card ${notif.is_active ? 'active' : 'inactive'}`}>

            {/* Card header */}
            <div className="notif-card-header">
              <div className="notif-card-left">
                <div className="notif-icon-wrap">
                  {notif.is_active ? <FaBell className="notif-icon on" /> : <FaBellSlash className="notif-icon off" />}
                </div>
                <div className="notif-card-info">
                  <div className="notif-card-title">{notif.label}</div>
                  <div className="notif-card-meta">
                    <span className="notif-module-badge">{notif.module}</span>
                    {notif.description && <span className="notif-description">{notif.description}</span>}
                  </div>
                </div>
              </div>

              <div className="notif-card-actions">
                {/* Toggle activo/inactivo */}
                <button
                  className={`notif-toggle ${notif.is_active ? 'toggle-on' : 'toggle-off'}`}
                  onClick={() => handleToggle(notif)}
                  disabled={loading}
                  title={notif.is_active ? 'Disable' : 'Enable'}
                >
                  <span className="toggle-knob" />
                </button>

                {/* Botón para gestionar recipients */}
                <button
                  className="btn-manage-recipients"
                  onClick={() => handleOpenRecipients(notif)}
                  disabled={loading}
                  title="Manage recipients"
                >
                  <FaUserPlus /> Recipients
                </button>
              </div>
            </div>

            {/* Recipients actuales */}
            <div className="notif-recipients-row">
              {notif.recipients.length === 0 ? (
                <span className="notif-no-recipients">No recipients assigned — notifications will not be sent</span>
              ) : (
                <div className="notif-recipients-list">
                  {notif.recipients.map(r => (
                    <div key={r.id_recipient} className="notif-recipient-chip">
                      <FaEnvelope className="chip-icon" />
                      <span className="chip-name">{r.name}</span>
                      <span className="chip-email">{r.email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        ))}
      </div>

      {/* MODAL RECIPIENTS */}
      {showRecipientsModal && selectedNotification && (
        <div className="modal-overlay" onClick={handleCloseRecipients}>
          <div className="modal-content modal-medium" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Recipients — {selectedNotification.label}</h2>
              <button className="btn-close" onClick={handleCloseRecipients}><FaTimes /></button>
            </div>

            <div className="modal-body">
              <p className="modal-hint">
                Select which users will receive an email when this notification is triggered.
              </p>

              {/* Buscador */}
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  autoFocus
                />
              </div>

              {/* Lista de usuarios */}
              <div className="notif-user-picker">
                {availableUsers.length === 0 ? (
                  <div className="notif-empty">No users assigned to this store.</div>
                ) : filteredUsers.length === 0 ? (
                  <div className="notif-empty">No users match your search.</div>
                ) : (
                  filteredUsers.map(user => {
                    const isSelected = selectedUserIds.includes(user.id_user);
                    return (
                      <div
                        key={user.id_user}
                        className={`notif-user-row ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleToggleUser(user.id_user)}
                      >
                        <div className="notif-user-check">
                          {isSelected ? '✓' : '○'}
                        </div>
                        <div className="notif-user-info">
                          <span className="notif-user-name">{user.name}</span>
                          <span className="notif-user-email">{user.email}</span>
                        </div>
                        <span className={`badge badge-${user.role}`}>
                          {user.role === 'admin' ? 'Admin' : 'Employee'}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="notif-selected-count">
                {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
              </div>
            </div>

            <div className="modal-footer">
              <div className="footer-totals"></div>
              <div className="footer-actions">
                <button className="btn-secondary" onClick={handleCloseRecipients} disabled={savingRecipients}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleSaveRecipients} disabled={savingRecipients}>
                  {savingRecipients ? 'Saving...' : `Save (${selectedUserIds.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notifications;